import React, { useRef, useState } from "react";
import { UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * MediaUpload — a compact file picker used inside admin editors.
 * Props: value (url), onChange(url), accept ("image/*" | "application/pdf"), label, testId
 */
const MediaUpload = ({ value, onChange, accept = "image/*", label = "Upload", testId = "media-upload" }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.upload(file);
      onChange(res.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isImage = accept?.startsWith("image");

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testId}-btn`}
          className="inline-flex items-center gap-2 px-4 py-2 border border-ink-line rounded-full text-sm text-ink-soft hover:text-ink hover:border-ink transition-colors disabled:opacity-50"
        >
          <UploadCloud size={14} /> {busy ? "Uploading…" : label}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testId}-clear`}
            className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-red-600"
          >
            <X size={12} /> Clear
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          data-testid={`${testId}-input`}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      {value && (
        <div className="text-xs">
          {isImage ? (
            <div className="inline-block border border-ink-line p-1 bg-paper max-w-[220px]">
              <img src={value} alt="" className="w-full h-auto max-h-32 object-contain" />
            </div>
          ) : (
            <a href={value} target="_blank" rel="noreferrer" className="underline text-ink hover:text-gold-dark break-all">
              {value}
            </a>
          )}
        </div>
      )}
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste a URL"
        data-testid={`${testId}-url-input`}
        className="w-full px-3 py-2 bg-paper border border-ink-line rounded text-xs text-ink outline-none focus:border-ink"
      />
    </div>
  );
};

export default MediaUpload;
